import PropTypes from "prop-types";
import React from "react";

import { Badge, Text, textPropTypes } from "@swa-ui/core";
import { classNames } from "@swa-ui/string";

import { ItineraryDate } from "../ItineraryDate";
import { ItineraryStation } from "../ItineraryStation";
import styles from "./ItineraryBadge.module.scss";

/**
 * ItineraryBadge renders a badge for variety of itinerary snippets, such as airport location or
 * travel date.
 */

export const ItineraryBadge = (props) => {
  const { className, height, items, padding, styleSetting, textProps } = props;

  return (
    <Badge {...getProps()}>
      <span className={getContainerClass()}>{items.map(renderLine)}</span>
    </Badge>
  );

  function renderLine(item) {
    const { content, format } = item;
    const key = `${content}-${format}`;
    let contentToRender;

    if (!format || format === "text") {
      contentToRender = (
        <Text {...getTextProps(item)} key={key}>
          {content}
        </Text>
      );
    } else if (isStationItem(format)) {
      contentToRender = <ItineraryStation {...getStationProps(item)} key={key} />;
    } else {
      contentToRender = <ItineraryDate {...getItineraryDateProps(item)} key={key} />;
    }

    return contentToRender;
  }

  function getProps() {
    const { background, border, foregroundColor } = styleSetting;

    return {
      border,
      className: classNames(className, styles.itineraryBadge),
      foregroundColor,
      height,
      padding,
      styleType: background,
    };
  }

  function getTextProps(item) {
    const { className: itemClassName, textProps: itemTextProps } = item;

    return {
      className: itemClassName,
      ...(itemTextProps ?? textProps),
    };
  }

  function getStationProps(item) {
    const { className: itemClassName, content, format, textProps: itemTextProps } = item;

    return {
      className: itemClassName,
      format: format,
      stationCode: content,
      textProps: itemTextProps ?? textProps,
    };
  }

  function getItineraryDateProps(item) {
    const { className: itemClassName, content, format, textProps: itemTextProps } = item;

    return {
      className: itemClassName,
      date: content,
      format,
      textProps: itemTextProps ?? textProps,
    };
  }

  function getContainerClass() {
    const { center, minimumWidth } = styleSetting;

    return classNames(styles.container, {
      [styles.center]: center,
      [styles.minimumWidthLarge]: minimumWidth === "large",
    });
  }

  function isStationItem(format) {
    return format === "stationCode" || format === "stationShortName" || format === "stationName";
  }
};

ItineraryBadge.propTypes = {
  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /**
   * Optional minimum height to be given to Background. For circle or square shapes, height will
   * also define the background's width.
   */
  height: PropTypes.oneOfType([
    PropTypes.oneOf(["large", "line", "lineHeight", "medium", "small", "xlarge", "xsmall"]),
    PropTypes.number,
  ]),

  /**
   * ItineraryDate, ItineraryStation, and Text are used to render the badge content.
   * Which component used will depend on the format given for each item in the list.
   *
   * content - what will be rendered by one of these components. Refer to the components for info.
   * format - how the content will be rendered. Refer to the components for info.
   * textProps - Props passed to the underlying Text component. See Text for more details.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      className: PropTypes.string,
      content: PropTypes.string,
      format: PropTypes.string,
      textProps: PropTypes.shape(textPropTypes),
    })
  ),

  /** Padding allows the caller to specify how much padding will be defined. */
  padding: PropTypes.oneOf(["large", "medium", "none", "small", "xlarge", "xsmall"]),

  /**
   * Display styling. See story representation for how these look. Also, good resource for props
   * are the core components Badge and Background which are used by ItineraryBadge directly or
   * indirectly.
   */
  styleSetting: PropTypes.shape({
    background: PropTypes.string,
    border: PropTypes.shape({
      color: PropTypes.string,
      radius: PropTypes.oneOf(["large", "medium", "small", "xlarge", "xsmall"]),
      style: PropTypes.oneOf(["dashed", "dotted", "double", "inset", "solid"]),
      width: PropTypes.oneOf(["large", "medium", "small", "xlarge", "xsmall"]),
    }),
    center: PropTypes.bool,
    minimumWidth: PropTypes.oneOf(["large", "small"]),
    padding: PropTypes.oneOf(["large", "medium", "none", "small", "xlarge", "xsmall"]),
  }),

  /** Props passed to the underlying Text component. See Text for more details. */
  textProps: PropTypes.shape(textPropTypes),
};

ItineraryBadge.defaultProps = {
  items: [],
  styleSetting: {
    background: "white",
    center: true,
    padding: "small",
  },
};
